import { MotifIcon } from "@ey-xd/motif-react";
import { IconoirList } from "@ey-xd/motif-icon";
import { getTotalOrderAmount } from "../helper";
import "./OrdersEstimateTable.scss";
import { ProductAccordian } from "../../../pages/Checkout/Components/OrderExpected/ProductAccordian";
import orderEmptyImg from "../../../assets/images/empty_order_pipeline.svg";
import { RichTextEditor } from "../../Form/RichTextEditor/RichTextEditor";
import EmptyOrder from "../../EmptyOrder";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import {
  NO_DRAFT_ESTIMATE_HEADING,
  NO_ESTIMATES_CREATED_YET,
  NO_DRAFTS_CREATED_YET,
  NO_ESTIMATE_TEXT,
  NO_DRAFT_TEXT,
  COST_MACRO_CATEGORY,
} from "../../../Constant";
import { fetchSkuConfigData } from "../../../store/slices/CTDBSkuConfigSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { useEffect, useRef, useState } from "react";
import {
  COST_CATEGORY_PLACEHOLDER,
  COST_METRIC_PLACEHOLDER,
} from "../../../pages/CreateSku/Constants";
import Loader from "../../Loader/Loader";
import { containsEmoji, sanitizeHTMLSpace } from "../../../utils/common";
import OrderItemsHeaderTooltip from "../OrderItemsHeaderTooltip/OrderItemsHeaderTooltip";

export interface ColumnConfig {
  key: string;
  label: string;
  render?: (row: any) => React.ReactNode;
}

interface OrdersEstimateTableProps {
  data: any[];
  columnsData: ColumnConfig[];
  setOrdersEstimate: React.Dispatch<React.SetStateAction<any[]>>;
  setShowPagination: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string;
  status: string;
  isFilterApplied: boolean;
}

const OrdersEstimateTable: React.FC<OrdersEstimateTableProps> = ({
  data,
  columnsData,
  setOrdersEstimate,
  setShowPagination,
  searchText,
  status,
  isFilterApplied,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const configData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [expandedTableWidth, setExpandedTableWidth] = useState<number>(0);

  useEffect(() => {
    const updateExpandedTableWidth = () => {
      if (!tableContainerRef.current) return;
      setExpandedTableWidth(tableContainerRef.current.clientWidth);
    };

    updateExpandedTableWidth();
    window.addEventListener("resize", updateExpandedTableWidth);

    const resizeObserver = new ResizeObserver(updateExpandedTableWidth);
    if (tableContainerRef.current) {
      resizeObserver.observe(tableContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateExpandedTableWidth);
      resizeObserver.disconnect();
    };
  }, [data, columnsData, configData]);

  const renderNoSearchResult = () => {
    if (isFilterApplied) {
      const header = searchText
        ? `No result under the filter and by the keyword "${searchText}"`
        : `No result under the filter`;

      const message = searchText
        ? "Did you not find any results? We recommend refining your search criteria and/or broadening your filter parameters to enhance your search outcomes."
        : "Did not find any results? We recommend broadening your filter criteria for a more comprehensive search.";

      return (
        <NoSearchResult
          searchValue={searchText}
          header={header}
          message={message}
        />
      );
    } else {
      return <NoSearchResult searchValue={searchText} />;
    }
  };

  if (Array.isArray(data) && data?.length === 0) {
    const hasEmoji = containsEmoji(searchText);
    if ((!searchText && !isFilterApplied) || hasEmoji) {
      return (
        <EmptyOrder
          image={orderEmptyImg}
          header={
            status === "estimate"
              ? NO_ESTIMATES_CREATED_YET
              : status === "draft"
                ? NO_DRAFTS_CREATED_YET
                : NO_DRAFT_ESTIMATE_HEADING
          }
          subHeader={
            status === "estimate"
              ? NO_ESTIMATE_TEXT
              : status === "draft"
                ? NO_DRAFT_TEXT
                : NO_ESTIMATE_TEXT
          }
        />
      );
    }
    return renderNoSearchResult();
  } else {
    if (configData === null && data && data.length > 0) {
      dispatch(fetchSkuConfigData());
    }

    if (!configData) {
      return <Loader />;
    } else {
      setShowPagination(true);
    }
    const { CostCategory, CostMacroCategory } = configData;

    const CostCategoryTypeOptions: { label: string; value: string }[] = [
      {
        label: COST_CATEGORY_PLACEHOLDER,
        value: "",
      },
    ];
    const CostMetricOptions: { label: string; value: string }[] = [
      {
        label: COST_METRIC_PLACEHOLDER,
        value: "",
      },
    ];

    const getCostCataegoryType = (order: any) => {
      if (CostCategory.length === 0) {
        return null;
      }
      return CostCategory.find(
        (cost) => cost.CostMetricCd === order.CostMetricCd,
      )?.CostMetricType;
    };

    const getCostMetric = (order: any) => {
      if (CostCategory.length === 0) {
        return null;
      }
      return CostCategory.find(
        (cost) => cost.CostMetricCd === order.CostMetricCd,
      )?.CostMetric;
    };

    const getCostMacroCategory = (order: any) => {
      if (CostMacroCategory.length === 0) {
        return null;
      }

      return CostMacroCategory.filter((cost) =>
        order.CostMacroCategoryCd.includes(cost.CostMacroCategoryCd),
      )
        .map((item) => item.Name)
        .join(", ");
    };

    const uniqueCostCategory: string[] = [];
    CostCategory?.forEach(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      (item: {
        Name: string;
        CostMetricType: string;
        CostMetricCd: string;
        CostMetric: string;
      }) => {
        CostMetricOptions.push({
          label: item.CostMetric,
          value: item.CostMetric,
        });
        if (uniqueCostCategory.includes(item.CostMetricType) === false) {
          uniqueCostCategory.push(item.CostMetricType);
          CostCategoryTypeOptions.push({
            label: item.CostMetricType,
            value: item.CostMetricType,
          });
        }
      },
    );

    const getClassNamesEstimateTable = (key: string) => {
      switch (key) {
        case "SkuId":
          return "estimate-sku-name";
        case "View":
          return "estimate-order-name";
        case "RequestedBy":
          return "estimate-requested-by";
        case "CreatedDtm":
          return "estimate-date-created";
        case "Amount":
          return "estimate-amount";
        case "StatusCd":
          return "estimate-status";
        default:
          return "";
      }
    };

    return (
      <div ref={tableContainerRef}>
        <table className="motif-table-standard estimate-main-table">
          <thead>
            <tr>
              {columnsData.map((col) => (
                <th
                  className={getClassNamesEstimateTable(col.key)}
                  key={col.key}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((order) => {
              return (
                <>
                  <tr>
                    {columnsData.map((col) => (
                      <td key={col.key}>
                        {col.render ? col.render(order) : order[col.key]}
                      </td>
                    ))}
                  </tr>
                  <tr
                    className={
                      order.isSKUExpanded
                        ? "product-list-wrapper"
                        : "product-list-wrapper hide-details"
                    }
                  >
                    <td
                      colSpan={8}
                      className="product-list order-success-content"
                    >
                      <>
                        <div className="product-list-header">
                          <MotifIcon
                            iconFunction={IconoirList}
                            size="20"
                            title="Order Bundle"
                          ></MotifIcon>
                          <span>SKU Details</span>
                        </div>
                        <div className="sku-details-content">
                          <div className="sku-details-desc-heading">
                            Description
                          </div>
                          <RichTextEditor
                            value={sanitizeHTMLSpace(order.Description)}
                            readOnly={true}
                          />
                          <div className="motif-container sku-detail-container">
                            <div className="motif-row sku-detail-row">
                              <div className="motif-col-lg-4">
                                <div className="label">Status</div>
                                <div className="value">{order.StatusName}</div>
                              </div>
                              <div className="motif-col-lg-4">
                                <div className="label">Cost</div>
                                <div className="value">
                                  {getTotalOrderAmount(order)}
                                </div>
                              </div>
                              <div className="motif-col-lg-4">
                                <div className="label">Returnable</div>
                                <div className="value">
                                  {order.IsReturnable ? "Yes" : "No"}
                                </div>
                              </div>
                            </div>
                            <div className="motif-row sku-detail-row">
                              <div className="motif-col-lg-4">
                                <div className="label">Cost Interval</div>
                                <div className="value">
                                  {order.CostIntervalCd}
                                </div>
                              </div>
                              <div className="motif-col-lg-4">
                                <div className="label">Cost Type</div>
                                <div className="value">
                                  {getCostCataegoryType(order)}
                                </div>
                              </div>
                              <div className="motif-col-lg-4">
                                <div className="label">Cost Metric</div>
                                <div className="value">
                                  {getCostMetric(order)}
                                </div>
                              </div>
                            </div>
                            <div className="motif-row sku-detail-row">
                              <div className="motif-col-lg-4">
                                <div className="label">
                                  {COST_MACRO_CATEGORY}
                                </div>
                                <div className="value">
                                  {getCostMacroCategory(order)}
                                </div>
                              </div>
                              <div className="motif-col-lg-4">
                                <div className="label">NSR</div>
                                <div className="value">
                                  {order.CostMetricNSR === null
                                    ? 0
                                    : order.CostMetricNSR}
                                </div>
                              </div>
                              <div className="motif-col-lg-4"></div>
                            </div>
                          </div>
                        </div>
                        <div className="product-list-footer">
                          <span
                            onClick={() =>
                              setOrdersEstimate(
                                data.map((o) =>
                                  o.OrderSkuId === order.OrderSkuId
                                    ? { ...o, isSKUExpanded: false }
                                    : o,
                                ),
                              )
                            }
                          >
                            Close Details
                          </span>
                        </div>
                      </>
                    </td>
                  </tr>

                  <tr
                    className={
                      order.isOrderExpanded
                        ? "product-list-wrapper"
                        : "product-list-wrapper hide-details"
                    }
                  >
                    <td
                      colSpan={6}
                      className="product-list order-success-content order-items-list"
                    >
                      <>
                        <div className="product-list-content">
                          <table
                            className="motif-table-standard"
                            style={
                              expandedTableWidth
                                ? {
                                    width: `${expandedTableWidth}px`,
                                    maxWidth: `${expandedTableWidth}px`,
                                  }
                                : undefined
                            }
                          >
                            <thead>
                              <tr>
                                <th className="order-items-sku-col">
                                  <OrderItemsHeaderTooltip
                                    label="SKU name/SKU ID"
                                    type="sku"
                                  />
                                </th>
                                <th className="order-items-engagement-col">
                                  Engagement code
                                </th>
                                <th className="text-right-align order-items-unit-cost-col">
                                  <OrderItemsHeaderTooltip
                                    label="Unit cost"
                                    type="unitCost"
                                  />
                                </th>
                                <th className="order-items-units-col">
                                  <OrderItemsHeaderTooltip
                                    label="Units per period"
                                    type="unitsPerPeriod"
                                  />
                                </th>
                                <th className="order-items-periods-col">
                                  <OrderItemsHeaderTooltip
                                    label="No. of periods"
                                    type="numberOfPeriods"
                                  />
                                </th>
                                <th className="text-right-align order-items-total-col">
                                  <OrderItemsHeaderTooltip
                                    label="Total cost"
                                    type="totalCost"
                                  />
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.OrderDetails &&
                                order.OrderDetails.length > 0 &&
                                order.OrderDetails.map((detail: any) => {
                                  return (
                                    <ProductAccordian
                                      key={detail.ProductName}
                                      productName={detail.ProductName}
                                      skuDetails={detail.SkuDetails}
                                      showEngagementColumn={true}
                                      isOrderpipelineOrderTab={true}
                                      showQuantityColumn={false}
                                    />
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    </td>
                  </tr>
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
};

export default OrdersEstimateTable;
